"use client"

import React, { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import {
  ChevronDown,
  ChevronRight,
  CreditCard,
  Mail,
  MessageCircle,
  Search,
  Smartphone,
  Wrench,
} from "lucide-react"

interface FaqItem {
  id: string
  cat: string
  q: string
  a: string
}

interface FaqsData {
  page: Record<string, string>
  categories: { id: string; label: string }[]
  items: FaqItem[]
}

interface Props {
  data: FaqsData
  lng: string
}

function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0
  while (remaining.length > 0) {
    const lm = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/)
    const bm = remaining.match(/\*\*([^*]+)\*\*/)
    let next: { type: "link" | "bold"; match: RegExpMatchArray } | null = null
    if (lm && (!bm || (lm.index ?? 0) <= (bm.index ?? 0))) {
      next = { type: "link", match: lm }
    } else if (bm) {
      next = { type: "bold", match: bm }
    }
    if (!next) {
      parts.push(remaining)
      break
    }
    const idx = next.match.index ?? 0
    if (idx > 0) parts.push(remaining.slice(0, idx))
    if (next.type === "link") {
      parts.push(
        <a
          key={key++}
          href={next.match[2]}
          className="text-home-orange_1 underline decoration-orange-200 underline-offset-2 hover:decoration-home-orange_1"
        >
          {next.match[1]}
        </a>
      )
    } else {
      parts.push(
        <strong key={key++} className="font-semibold text-stone-900">
          {next.match[1]}
        </strong>
      )
    }
    remaining = remaining.slice(idx + next.match[0].length)
  }
  return parts
}

function renderAnswer(text: string): React.ReactNode {
  const lines = text.split("\n")
  const nodes: React.ReactNode[] = []
  let listBuf: { type: "ul" | "ol"; items: string[] } | null = null
  const flush = () => {
    if (!listBuf) return
    const Tag = listBuf.type === "ul" ? "ul" : "ol"
    const className =
      listBuf.type === "ol"
        ? "my-2 list-decimal space-y-1.5 pl-5"
        : "my-2 list-disc space-y-1.5 pl-5"
    nodes.push(
      <Tag key={`list-${nodes.length}`} className={className}>
        {listBuf.items.map((line, i) => (
          <li key={i}>{renderInline(line)}</li>
        ))}
      </Tag>
    )
    listBuf = null
  }
  for (const line of lines) {
    if (line.trim() === "") {
      flush()
      continue
    }
    const ol = line.match(/^\d+\.\s+(.+)$/)
    const ul = line.match(/^-\s+(.+)$/)
    if (ol) {
      if (listBuf?.type !== "ol") {
        flush()
        listBuf = { type: "ol", items: [] }
      }
      listBuf.items.push(ol[1])
    } else if (ul) {
      if (listBuf?.type !== "ul") {
        flush()
        listBuf = { type: "ul", items: [] }
      }
      listBuf.items.push(ul[1])
    } else {
      flush()
      nodes.push(
        <p key={nodes.length} className="my-2">
          {renderInline(line)}
        </p>
      )
    }
  }
  flush()
  return nodes
}

export default function HelpCenterClient({ data, lng }: Props) {
  const [query, setQuery] = useState("")
  const [openItems, setOpenItems] = useState<Set<string>>(new Set())

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return data.items
    return data.items.filter(
      (it) =>
        it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
    )
  }, [data.items, query])

  const grouped = useMemo(() => {
    const m = new Map<string, FaqItem[]>()
    for (const cat of data.categories) m.set(cat.id, [])
    for (const it of filtered) m.get(it.cat)?.push(it)
    return m
  }, [data.categories, filtered])

  useEffect(() => {
    const hash = window.location.hash.replace(/^#faq-/, "")
    if (hash && data.items.some((i) => i.id === hash)) {
      setOpenItems(new Set([hash]))
      setTimeout(() => {
        document
          .getElementById(`faq-${hash}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 100)
    }
  }, [data.items])

  const toggle = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const ui = data.page
  const { homeLabel, pageTitle, pageSubtitle, quickAccessLabel, faqSectionLabel } = ui
  const totalCount = data.items.length
  const waNumber = data.page.contactWhatsAppValue?.replace(/[^\d]/g, "") ?? ""

  const quickLinks = [
    {
      href: `/${lng}/installation-guide`,
      icon: Wrench,
      title: ui.installTitle,
      desc: ui.installDesc,
    },
    {
      href: `/${lng}/refund-policy`,
      icon: CreditCard,
      title: ui.refundTitle,
      desc: ui.refundDesc,
    },
    {
      href: `/${lng}/esim-compatible-list`,
      icon: Smartphone,
      title: ui.compatibleTitle,
      desc: ui.compatibleDesc,
    },
    {
      href: `/${lng}/contact`,
      icon: MessageCircle,
      title: ui.contactTitle,
      desc: ui.contactDesc,
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section>
        <div className="mx-auto max-w-4xl px-5 pb-10 pt-8 md:px-10 md:pb-14 md:pt-12">
          <nav aria-label="Breadcrumb" className="mb-6 text-sm text-stone-400">
            <ol className="flex items-center gap-2">
              <li>
                <Link
                  href={`/${lng}`}
                  className="transition-colors hover:text-stone-700"
                >
                  {homeLabel}
                </Link>
              </li>
              <li>/</li>
              <li className="text-stone-700">{pageTitle}</li>
            </ol>
          </nav>
          <h1 className="mb-3 text-3xl font-extrabold text-stone-900 md:text-4xl">
            {pageTitle}
          </h1>
          <p className="mb-8 text-base text-stone-500 md:text-lg">
            {pageSubtitle}
          </p>
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={data.page.searchPlaceholder}
              className="w-full rounded-2xl border border-stone-200 bg-white py-4 pl-12 pr-4 text-base text-stone-800 placeholder:text-stone-400 focus:border-home-orange_1 focus:outline-none focus:ring-2 focus:ring-orange-100"
            />
          </div>
        </div>
      </section>

      {/* Quick access cards */}
      <section>
        <div className="mx-auto max-w-4xl px-5 pb-10 md:px-10 md:pb-14">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-stone-500">
            {quickAccessLabel}
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {quickLinks.map((link) => {
              const Icon = link.icon
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className="group flex flex-col rounded-2xl border border-stone-200 bg-white p-4 transition-shadow hover:shadow-md md:p-5"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-home-orange_1/10">
                    <Icon className="h-5 w-5 text-home-orange_1" aria-hidden />
                  </div>
                  <p className="text-sm font-bold text-stone-900 md:text-base">
                    {link.title}
                  </p>
                  <p className="mt-1 text-xs text-stone-500 md:text-[13px]">
                    {link.desc}
                  </p>
                  <ChevronRight
                    className="mt-2 h-4 w-4 text-stone-400 transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* FAQ section heading */}
      <section>
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <h2 className="text-xl font-bold text-stone-900 md:text-2xl">
            {faqSectionLabel}
          </h2>
        </div>
      </section>

      {/* Sticky category chips */}
      <div className="sticky top-0 z-10 bg-white">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <div className="flex gap-2 overflow-x-auto py-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <a
              href="#cat-all"
              className="shrink-0 rounded-full bg-stone-100 px-3.5 py-1.5 text-sm font-medium text-stone-700 transition-colors hover:bg-orange-100/60 hover:text-home-orange_1"
            >
              {data.page.categoryAllLabel} ({filtered.length}/{totalCount})
            </a>
            {data.categories.map((cat) => {
              const count = grouped.get(cat.id)?.length ?? 0
              if (count === 0) return null
              return (
                <a
                  key={cat.id}
                  href={`#cat-${cat.id}`}
                  className="shrink-0 rounded-full bg-stone-100 px-3.5 py-1.5 text-sm font-medium text-stone-700 transition-colors hover:bg-orange-100/60 hover:text-home-orange_1"
                >
                  {cat.label} ({count})
                </a>
              )
            })}
          </div>
        </div>
      </div>

      {/* FAQ list */}
      <div
        id="cat-all"
        className="mx-auto max-w-4xl scroll-mt-24 px-5 py-6 md:px-10 md:py-8"
      >
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-stone-200 bg-white py-16 text-center">
            <p className="text-lg font-semibold text-stone-700">
              {data.page.noResultsTitle}
            </p>
            <p className="mt-2 text-sm text-stone-500">
              {data.page.noResultsDesc}
            </p>
          </div>
        ) : (
          data.categories.map((cat) => {
            const items = grouped.get(cat.id)
            if (!items?.length) return null
            return (
              <section
                key={cat.id}
                id={`cat-${cat.id}`}
                className="mb-10 scroll-mt-24"
              >
                <h3 className="mb-4 text-lg font-bold text-stone-900 md:text-xl">
                  {cat.label}
                </h3>
                <div className="space-y-2">
                  {items.map((item) => {
                    const isOpen = openItems.has(item.id)
                    return (
                      <div
                        key={item.id}
                        id={`faq-${item.id}`}
                        className="scroll-mt-24 rounded-2xl border border-stone-200 bg-white shadow-sm transition-shadow hover:shadow"
                      >
                        <button
                          type="button"
                          onClick={() => toggle(item.id)}
                          className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left md:px-6 md:py-5"
                          aria-expanded={isOpen}
                          aria-controls={`faq-body-${item.id}`}
                        >
                          <span className="text-base font-semibold text-stone-900 md:text-lg">
                            {item.q}
                          </span>
                          <ChevronDown
                            className={`h-5 w-5 shrink-0 text-stone-400 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            }`}
                            aria-hidden
                          />
                        </button>
                        {isOpen && (
                          <div
                            id={`faq-body-${item.id}`}
                            className="px-5 pb-5 text-sm leading-[1.7] text-stone-700 md:px-6 md:pb-6 md:text-base"
                          >
                            {renderAnswer(item.a)}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </section>
            )
          })
        )}
      </div>

      {/* Still need help */}
      <section>
        <div className="mx-auto max-w-4xl px-5 pb-16 md:px-10 md:pb-20">
          <h2 className="text-center text-2xl font-bold text-stone-900 md:text-3xl">
            {data.page.stillNeedHelpTitle}
          </h2>
          <p className="mt-3 text-center text-stone-500">
            {data.page.stillNeedHelpSubtitle}
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            <button
              type="button"
              onClick={() => {
                const w = window as unknown as {
                  $crisp?: { push: (args: unknown[]) => void }
                }
                if (w.$crisp) w.$crisp.push(["do", "chat:open"])
              }}
              className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-stone-200 bg-white p-6 text-center transition-shadow hover:shadow-md"
            >
              <MessageCircle className="h-8 w-8 text-home-orange_1" />
              <p className="mt-4 text-base font-semibold text-stone-900">
                {data.page.contactLiveChat}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {data.page.contactLiveChatNote}
              </p>
            </button>
            <a
              href={waNumber ? `https://wa.me/${waNumber}` : "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center rounded-2xl border border-stone-200 bg-white p-6 text-center transition-shadow hover:shadow-md"
            >
              <svg
                className="h-8 w-8 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden
              >
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.594 5.351l-.999 3.648 3.894-.698zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01a1.094 1.094 0 00-.793.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.711.306 1.264.489 1.696.626.713.226 1.361.194 1.874.118.572-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
              </svg>
              <p className="mt-4 text-base font-semibold text-stone-900">
                {data.page.contactWhatsApp}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {data.page.contactWhatsAppValue}
              </p>
            </a>
            <a
              href={`mailto:${data.page.contactEmailValue}`}
              className="flex flex-col items-center justify-center rounded-2xl border border-stone-200 bg-white p-6 text-center transition-shadow hover:shadow-md"
            >
              <Mail className="h-8 w-8 text-stone-600" />
              <p className="mt-4 text-base font-semibold text-stone-900">
                {data.page.contactEmail}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {data.page.contactEmailValue}
              </p>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
